// src/redux/slices/industryAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { industriesService } from '../../services/industriesService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchIndustriesAdmin = createAsyncThunk(
    'industryAdmin/fetchIndustriesAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await industriesService.getIndustries({
                ...params,
                limit: params.limit || 10,
                page: params.page || 1
            })
            return {
                industries: response.data.industries,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createIndustry = createAsyncThunk(
    'industryAdmin/createIndustry',
    async (industryData, { rejectWithValue }) => {
        try {
            const response = await industriesService.createIndustry(industryData)
            return response.data.industry
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateIndustry = createAsyncThunk(
    'industryAdmin/updateIndustry',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await industriesService.updateIndustry(id, data)
            return response.data.industry
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteIndustry = createAsyncThunk(
    'industryAdmin/deleteIndustry',
    async (id, { rejectWithValue }) => {
        try {
            await industriesService.deleteIndustry(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const industryAdminSlice = createSlice({
    name: 'industryAdmin',
    initialState: {
        industries: [],
        currentIndustry: null,
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentIndustry: (state) => {
            state.currentIndustry = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Industries Admin
            .addCase(fetchIndustriesAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchIndustriesAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.industries = action.payload.industries
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchIndustriesAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Create Industry
            .addCase(createIndustry.pending, (state) => {
                state.loading = true
            })
            .addCase(createIndustry.fulfilled, (state, action) => {
                state.loading = false
                state.industries.unshift(action.payload)
                state.error = null
            })
            .addCase(createIndustry.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Update Industry
            .addCase(updateIndustry.pending, (state) => {
                state.loading = true
            })
            .addCase(updateIndustry.fulfilled, (state, action) => {
                state.loading = false
                const index = state.industries.findIndex(industry => industry._id === action.payload._id)
                if (index !== -1) {
                    state.industries[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateIndustry.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Delete Industry
            .addCase(deleteIndustry.fulfilled, (state, action) => {
                state.industries = state.industries.filter(industry => industry._id !== action.payload)
                state.error = null
            })
    }
})

export const { clearCurrentIndustry, clearError, setPagination } = industryAdminSlice.actions
export default industryAdminSlice.reducer